import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  IGroupDetail,
  useGroupDebtorCandidates,
  useGroupDeepLinkState,
  useGroupPermissions,
  useGroupRoster,
  useRemindDebtors,
  useStudentPermissions,
  useToasts,
} from 'core/hooks';
import { LmsBlockModal } from 'molecules';
import {
  filterAndSearch,
  selectDebtors,
  sortRoster,
  RosterSortKey,
} from 'core/utils/groupRoster';
import {
  downloadRosterCsv,
  IRosterCsvHeaders,
  IRosterExportRow,
} from 'core/utils/exportCsv';
import { GlobalToastTypeNames, ToastTypes } from 'components/types';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../../_detailShared/detail.styled';
import { StudentRosterFilters } from '../parts/StudentRosterFilters';
import { StudentRosterRow } from '../parts/StudentRosterRow';
import { AddStudentToGroupInlinePopup } from '../parts/AddStudentToGroupInlinePopup';
import { IGroupRosterRow } from '../parts/rosterTypes';
import { TabStateView } from '../parts/TabStateView';
import { GroupColors } from '../parts/groupColors';

interface IStudentsTabProps {
  group: IGroupDetail;
  onChanged: () => void;
}

/** Student full name (first/middle/last) for export + reminder summaries. */
const fullNameOf = (row: IGroupRosterRow): string =>
  [
    row.student.user?.firstName,
    row.student.user?.middleName,
    row.student.user?.lastName,
  ]
    .filter(Boolean)
    .join(' ') || '—';

/**
 * StudentsTab — «кто кому что должен» для группы. The centerpiece of the
 * control center: one row per agreement, per-student money in the agreement
 * currency, inline Move/Terminate/Open actions, bulk debtor reminders, and
 * roster export.
 *
 * Data flow (Req 9.2–9.4): rows are sourced from the dedicated, lazily-loaded
 * `useGroupRoster(group.id)` query — NOT the slimmed `GroupDetailQuery`, which
 * no longer carries the heavy inline `agreements[].contributions` payload. The
 * `group` prop is kept only for the id (roster query + terminate), the group
 * name (export filename), and currency context. The content is wrapped in
 * `TabStateView` so loading / error (with retry) / empty are visually distinct
 * (Req 14); a background refresh dims the populated table rather than blanking
 * it.
 *
 * Filter / search / sort (Req 8, 15.1, 15.3): the tab keeps no local state for
 * these — they live in the URL via `useGroupDeepLinkState` so the view survives
 * a reload and is shareable. `sortRoster(filterAndSearch(rows, filter, query),
 * sort)` applies sorting AFTER filter + search, so sorting reorders only the
 * currently visible rows (Req 8.6). Clicking a sorted column toggles asc/desc.
 *
 * Actions: one-click Terminate lives on the row (`StudentRosterRow`); the bulk
 * "remind all debtors" (Req 10.4–10.6) acts on currently-visible rows with
 * `debt > 0` and reports partial failures while preserving successes. Action
 * controls are role-gated via `useGroupPermissions` (Req 10.7).
 */
export const StudentsTab = memo(({ group, onChanged }: IStudentsTabProps) => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const { canTerminate, canRemindDebtors } = useGroupPermissions();
  const { canBlockLmsAccess } = useStudentPermissions();

  const { rows, loading, error, refetch } = useGroupRoster(group.id);

  // LMS block/unblock straight from the roster row (spec 0003 follow-up).
  // The candidate set gates the «block» action (debtors only) and feeds the
  // overdue amount to the badge; the mutations already refetch the group's
  // DebtorCandidatesList, so the Finances-tab debtors panel stays in sync.
  const {
    candidates,
    mutating: lmsMutating,
    block: lmsBlock,
    unblock: lmsUnblock,
  } = useGroupDebtorCandidates(group.id);

  const candidateById = useMemo(
    () => new Map(candidates.map((c) => [c.studentId, c])),
    [candidates],
  );

  const [lmsTarget, setLmsTarget] = useState<{
    studentId: string;
    mode: 'block' | 'unblock';
  } | null>(null);
  const {
    state: { rosterFilter, rosterQuery, rosterSort },
    setRosterFilter,
    setRosterQuery,
    setRosterSort,
  } = useGroupDeepLinkState();

  const {
    remindDebtors,
    pending: reminding,
  } = useRemindDebtors<IGroupRosterRow>();

  const [appendOpen, setAppendOpen] = useState(false);

  /** Roster refresh after a successful row mutation: roster + group KPIs. */
  const handleChanged = useCallback(() => {
    refetch();
    onChanged();
  }, [refetch, onChanged]);

  const onToggleLmsBlock = useCallback(
    (studentId: string, isBlocked: boolean) => {
      setLmsTarget({ studentId, mode: isBlocked ? 'unblock' : 'block' });
    },
    [],
  );

  const onLmsSubmit = useCallback(
    async (reason?: string) => {
      if (!lmsTarget) return;
      const ok =
        lmsTarget.mode === 'unblock'
          ? await lmsUnblock(lmsTarget.studentId)
          : await lmsBlock(lmsTarget.studentId, reason || undefined);
      if (ok) {
        setLmsTarget(null);
        handleChanged();
      }
    },
    [lmsTarget, lmsBlock, lmsUnblock, handleChanged],
  );

  /** Filter + search, then sort — sorting reorders visible rows only (Req 8.6). */
  const visible = useMemo(
    () =>
      sortRoster(filterAndSearch(rows, rosterFilter, rosterQuery), rosterSort),
    [rows, rosterFilter, rosterQuery, rosterSort],
  );

  const debtors = useMemo(() => selectDebtors(visible), [visible]);

  /** Toggle the active sort: same column flips dir, a new column starts asc. */
  const onSort = useCallback(
    (key: RosterSortKey) => {
      if (rosterSort && rosterSort.key === key) {
        setRosterSort({
          key,
          dir: rosterSort.dir === 'asc' ? 'desc' : 'asc',
        });
      } else {
        setRosterSort({ key, dir: 'asc' });
      }
    },
    [rosterSort, setRosterSort],
  );

  const onAddStudent = useCallback(() => setAppendOpen(true), []);

  const onExport = useCallback(() => {
    const exportRows: IRosterExportRow[] = visible.map((row) => ({
      studentName: fullNameOf(row),
      agreementTitle: row.agreementTitle || '',
      currency: row.currency || '',
      budget: Number(row.budgetAmount) || 0,
      paid: Number(row.paidAmount) || 0,
    }));

    const headers: IRosterCsvHeaders = {
      studentName: t('pages.groupDetail.students.col.student'),
      agreementTitle: t('pages.groupDetail.students.col.agreement'),
      currency: t('pages.groupDetail.students.export.currency'),
      budget: t('pages.groupDetail.students.col.budget'),
      paid: t('pages.groupDetail.students.col.paid'),
      debt: t('pages.groupDetail.students.col.debt'),
    };

    const filename = t('pages.groupDetail.students.export.filename', {
      group: group.name,
    });

    downloadRosterCsv(exportRows, headers, filename);
  }, [visible, t, group.name]);

  const onRemindDebtors = useCallback(async () => {
    if (reminding || debtors.length === 0) return;

    const { succeeded, failed } = await remindDebtors(debtors);

    if (failed.length === 0) {
      addToast({
        data: {
          title: t('pages.groupDetail.students.remind.successTitle'),
          text: t('pages.groupDetail.students.remind.successText', {
            count: succeeded.length,
          }),
          type: ToastTypes.success,
        },
        type: GlobalToastTypeNames.Finances,
      });
      return;
    }

    const failedNames = failed.map(({ item }) => fullNameOf(item)).join(', ');
    addToast({
      data: {
        title: t('pages.groupDetail.students.remind.partialTitle'),
        text: t('pages.groupDetail.students.remind.partialText', {
          succeeded: succeeded.length,
          failed: failed.length,
          names: failedNames,
        }),
        type: succeeded.length > 0 ? ToastTypes.warning : ToastTypes.error,
      },
      type: GlobalToastTypeNames.Finances,
    });
  }, [reminding, debtors, remindDebtors, addToast, t]);

  return (
    <div>
      <StudentRosterFilters
        filter={rosterFilter}
        setFilter={setRosterFilter}
        query={rosterQuery}
        setQuery={setRosterQuery}
        total={rows.length}
        filtered={visible.length}
        onAddStudent={onAddStudent}
      />

      <Toolbar>
        <L.PrimaryActionButton
          type="button"
          onClick={onExport}
          disabled={visible.length === 0}
        >
          {t('pages.groupDetail.students.export.button')}
        </L.PrimaryActionButton>
        {canRemindDebtors ? (
          <L.PrimaryActionButton
            type="button"
            onClick={onRemindDebtors}
            disabled={reminding || debtors.length === 0}
          >
            {t('pages.groupDetail.students.remind.button', {
              count: debtors.length,
            })}
          </L.PrimaryActionButton>
        ) : null}
      </Toolbar>

      <TabStateView
        loading={loading && rows.length === 0}
        error={error}
        isEmpty={!loading && !error && rows.length === 0}
        onRetry={refetch}
      >
        {visible.length === 0 ? (
          <S.EmptyState>
            {t('pages.groupDetail.students.emptyFiltered')}
          </S.EmptyState>
        ) : (
          <S.DimWhenFetching fetching={loading}>
            <S.Card>
              <L.SmallTable>
                <thead>
                  <tr>
                    <SortableHeader
                      label={t('pages.groupDetail.students.col.student')}
                      sortKey="name"
                      activeKey={rosterSort?.key}
                      dir={rosterSort?.dir}
                      onSort={onSort}
                    />
                    <SortableHeader
                      label={t('pages.groupDetail.students.col.agreement')}
                      sortKey="status"
                      activeKey={rosterSort?.key}
                      dir={rosterSort?.dir}
                      onSort={onSort}
                    />
                    <th>{t('pages.groupDetail.students.col.budget')}</th>
                    <SortableHeader
                      label={t('pages.groupDetail.students.col.paid')}
                      sortKey="percentPaid"
                      activeKey={rosterSort?.key}
                      dir={rosterSort?.dir}
                      onSort={onSort}
                    />
                    <SortableHeader
                      label={t('pages.groupDetail.students.col.debt')}
                      sortKey="debt"
                      activeKey={rosterSort?.key}
                      dir={rosterSort?.dir}
                      onSort={onSort}
                    />
                    <CenterTh>
                      {t('pages.groupDetail.students.col.contributions')}
                    </CenterTh>
                    <th>{t('pages.groupDetail.students.col.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <StudentRosterRow
                      key={row.id}
                      row={row}
                      canTerminate={canTerminate}
                      canBlockLms={canBlockLmsAccess}
                      candidate={candidateById.get(row.student.id)}
                      lmsMutating={lmsMutating}
                      onToggleLmsBlock={onToggleLmsBlock}
                      onChanged={handleChanged}
                    />
                  ))}
                </tbody>
              </L.SmallTable>
            </S.Card>
          </S.DimWhenFetching>
        )}
      </TabStateView>

      {appendOpen ? (
        <AddStudentToGroupInlinePopup
          group={group}
          onClose={() => setAppendOpen(false)}
          onAdded={handleChanged}
        />
      ) : null}

      {lmsTarget ? (
        <LmsBlockModal
          key={`${lmsTarget.studentId}:${lmsTarget.mode}`}
          mode={lmsTarget.mode}
          loading={lmsMutating}
          onSubmit={onLmsSubmit}
          onCancel={() => setLmsTarget(null)}
        />
      ) : null}
    </div>
  );
});

interface ISortableHeaderProps {
  label: string;
  sortKey: RosterSortKey;
  activeKey?: RosterSortKey;
  dir?: 'asc' | 'desc';
  onSort: (key: RosterSortKey) => void;
}

/** Clickable, keyboard-operable roster column header with a sort indicator. */
const SortableHeader = memo(
  ({ label, sortKey, activeKey, dir, onSort }: ISortableHeaderProps) => {
    const isActive = activeKey === sortKey;
    const ariaSort = isActive
      ? dir === 'asc'
        ? 'ascending'
        : 'descending'
      : 'none';

    return (
      <th aria-sort={ariaSort}>
        <SortButton
          type="button"
          active={isActive}
          onClick={() => onSort(sortKey)}
        >
          {label}
          <SortGlyph>{isActive ? (dir === 'asc' ? '▲' : '▼') : '↕'}</SortGlyph>
        </SortButton>
      </th>
    );
  },
);

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 8px;
`;

const CenterTh = styled.th`
  text-align: center;
`;

const SortButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  color: ${({ active }) =>
    active ? GroupColors.accent : GroupColors.textMuted};
  font-weight: ${({ active }) => (active ? 700 : 600)};

  &:hover {
    color: ${GroupColors.accent};
  }
`;

const SortGlyph = styled.span`
  font-size: 9px;
  line-height: 1;
`;
